import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

/**
 * Listens for `beforeinstallprompt` and shows a sticky banner so the user can install the PWA.
 * Also handles iOS Safari (no native prompt) with a tip on how to add to Home Screen.
 * Dismissal is remembered for 7 days via localStorage.
 */
const DISMISS_KEY = "vsm_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
}

function wasDismissedRecently() {
  const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  if (!ts) return false;
  const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_DAYS;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    if (isIos()) {
      // iOS doesn't support beforeinstallprompt — show a tip once
      setShowIosTip(true);
      setHidden(false);
      return;
    }

    // If the event already fired before React mounted, pick it up from the early-capture
    if (window.__vsmDeferredInstall) {
      setDeferred(window.__vsmDeferredInstall);
      setHidden(false);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setHidden(false);
    };
    const onAvailable = () => {
      if (window.__vsmDeferredInstall) {
        setDeferred(window.__vsmDeferredInstall);
        setHidden(false);
      }
    };
    const onInstalled = () => {
      setDeferred(null);
      setHidden(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("vsm:install-available", onAvailable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("vsm:install-available", onAvailable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      console.warn("PWA userChoice rejected:", e);
    }
    setDeferred(null);
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md z-50 vsm-surface border vsm-border shadow-lg"
    >
      <div className="p-4 flex items-start gap-3">
        <img src="/ramki-logo.jpg" alt="Ramki" className="w-10 h-10 object-contain bg-white shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="vsm-overline">Install App</div>
          <div className="font-semibold text-sm">Vehicle SIM Maintenance</div>
          {showIosTip ? (
            <p className="text-xs vsm-muted mt-1 leading-relaxed">
              Tap the Share icon ⎋ in Safari and choose <strong>&quot;Add to Home Screen&quot;</strong> to install.
            </p>
          ) : (
            <p className="text-xs vsm-muted mt-1">
              Add the app to your home screen for full-screen access and offline shell.
            </p>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="install-prompt-dismiss"
          className="p-1 vsm-muted hover:opacity-100 shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {!showIosTip && (
        <div className="border-t vsm-border p-3 flex gap-2 justify-end">
          <Button
            variant="outline"
            className="rounded-none h-9"
            onClick={dismiss}
            data-testid="install-prompt-later"
          >
            Later
          </Button>
          <Button
            className="rounded-none vsm-primary-bg hover:opacity-90 h-9"
            onClick={install}
            data-testid="install-prompt-install"
          >
            <Download className="w-4 h-4 mr-2" /> Install
          </Button>
        </div>
      )}
    </div>
  );
}
